// ─────────────────────────────────────────────────────────────────────────────
// useCustomPresets
//
// Loads and manages the current user's CUSTOM (self-created) rule presets.
// Unlike account rules, these are scoped to (userId) only — not per-account —
// so a preset saved from one account can be applied to any account the user
// creates.
//
// Table: user_rule_presets (user_id, name, description, rules jsonb, created_at)
// `rules` is a plain array of { rule_type, parameters, severity }, mirroring
// the shape of RULE_PRESETS[x].rules in lib/rules.js — no account-specific
// ids, so it can be replayed onto any account via applyCustomPreset (see
// useUserRules.js).
//
// Uses authFetch (which wraps ensureValidToken + authHeaders + fetch)
// so token refresh is handled automatically before each request.
// ─────────────────────────────────────────────────────────────────────────────

import { useEffect, useCallback, useState } from "react";
import { SUPABASE_URL, authFetch } from "../lib/supabase";

export function useCustomPresets(userId) {
  const [presets, setPresets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    if (!userId || !SUPABASE_URL) {
      setPresets([]);
      setLoading(false);
      return;
    }
    try {
      const res = await authFetch(
        `${SUPABASE_URL}/rest/v1/user_rule_presets?user_id=eq.${userId}&order=created_at.desc&select=*`
      );
      if (!res.ok) {
        const txt = await res.text();
        console.error("useCustomPresets fetch:", res.status, txt);
        setError(txt);
        setPresets([]);
      } else {
        const data = await res.json();
        setPresets(Array.isArray(data) ? data : []);
        setError(null);
      }
    } catch (e) {
      console.error("useCustomPresets error:", e);
      setError(String(e));
      setPresets([]);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    setLoading(true);
    refresh();
  }, [refresh]);

  /**
   * Save a new custom preset from a list of rule rows (typically the
   * current account's `rules` from useUserRules). Strips account/user-
   * specific fields down to just rule_type/parameters/severity so the
   * preset can be replayed onto any account later.
   */
  const createPreset = useCallback(async (name, ruleRows, description = null) => {
    if (!userId || !name?.trim() || !Array.isArray(ruleRows) || ruleRows.length === 0) {
      return null;
    }
    const body = {
      user_id: userId,
      name: name.trim(),
      description: description?.trim() || null,
      rules: ruleRows.map(r => ({
        rule_type: r.rule_type,
        parameters: r.parameters,
        severity: r.severity || "warn",
      })),
    };
    try {
      const res = await authFetch(`${SUPABASE_URL}/rest/v1/user_rule_presets`, {
        method: "POST",
        headers: { Prefer: "return=representation" },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        console.error("createPreset failed:", res.status, await res.text());
        return null;
      }
      const data = await res.json();
      const created = Array.isArray(data) ? data[0] : data;
      await refresh();
      return created;
    } catch (e) {
      console.error("createPreset error:", e);
      return null;
    }
  }, [userId, refresh]);

  const deletePreset = useCallback(async (presetId) => {
    if (!userId || !presetId) return false;
    try {
      const res = await authFetch(
        `${SUPABASE_URL}/rest/v1/user_rule_presets?id=eq.${presetId}&user_id=eq.${userId}`,
        { method: "DELETE" }
      );
      if (!res.ok) {
        console.error("deletePreset failed:", res.status, await res.text());
        return false;
      }
      await refresh();
      return true;
    } catch (e) {
      console.error("deletePreset error:", e);
      return false;
    }
  }, [userId, refresh]);

  return {
    presets,
    loading,
    error,
    refresh,
    createPreset,
    deletePreset,
  };
}
